import * as React from 'react'
import {runInAction} from 'mobx'
import {observer} from 'mobx-react-lite'
import {
    Paper,
    Grid,
    List,
    ListItem,
    Typography,
    Tooltip,
    IconButton,
    Link,
    ListItemText,
    TextField,
    ListItemSecondaryAction
} from '@mui/material'
import {TextValidator} from 'react-material-ui-form-validator'
import {AddBox, DeleteForever, Language} from '@mui/icons-material'
import {makeStyles} from '@oc/styles'

interface ItemProps {
    item: string
}

interface Props {
    title: string
    listName: string
    addTooltip: string
    items: string[]
    setDirty?: () => void
    validators?: string[]
    errorMessages?: string[]
    readOnly?: boolean
    action?: (item: string) => React.ReactNode
    Action?: React.FunctionComponent<ItemProps>
}

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(2)
    },
    button: {
        marginLeft: theme.spacing(1)
    },
    textfield: {
        width: '95%'
    }
}))

export function OpeningLink(props: ItemProps) {
    const {item} = props
    return (
        <Tooltip title="Open link">
            <Link href={item} target="_blank" rel="noreferrer" color="inherit" style={{textDecoration: 'none'}}>
                <IconButton edge="end" aria-label="open-link" size="large">
                    <Language />
                </IconButton>
            </Link>
        </Tooltip>
    )
}

function ItemList(props: Props) {
    const {title, listName, addTooltip, items, setDirty, validators, errorMessages, readOnly, action, Action} = props
    const {classes} = useStyles()

    const addItem = React.useCallback(() => {
        runInAction(() => items.push(''))
        setDirty && setDirty()
    }, [items, setDirty])

    const changeItem = React.useCallback(
        (value: string, index: number) => {
            runInAction(() => (items[index] = value))
            setDirty && setDirty()
        },
        [items, setDirty]
    )

    const deleteItem = React.useCallback(
        (index: number) => {
            runInAction(() => items.splice(index, 1))
            setDirty && setDirty()
        },
        [items, setDirty]
    )

    return (
        <Paper square className={classes.paper}>
            <Grid container alignItems="center" justifyContent="space-between" direction="row">
                <Typography>{title}</Typography>
                {!readOnly ? (
                    <Tooltip title={addTooltip}>
                        <span>
                            <IconButton size="small" onClick={addItem} className={classes.button}>
                                <AddBox />
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
            </Grid>
            <List>
                {items.map((item, idx) => (
                    <ListItem key={`${listName}-${idx}`}>
                        <ListItemText
                            primary={
                                validators ? (
                                    <TextValidator
                                        className={classes.textfield}
                                        value={item}
                                        InputProps={{
                                            readOnly: !!readOnly
                                        }}
                                        name={`${listName}-${idx}`}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            changeItem(e.target.value, idx)
                                        }
                                        validators={validators}
                                        errorMessages={errorMessages}
                                    />
                                ) : (
                                    <TextField
                                        className={classes.textfield}
                                        value={item}
                                        onChange={e => changeItem(e.target.value, idx)}
                                        slotProps={{
                                            input: {
                                                readOnly: !!readOnly
                                            }
                                        }}
                                    />
                                )
                            }
                        />

                        <ListItemSecondaryAction>
                            <Grid>
                                {!readOnly ? (
                                    <Tooltip title="Delete">
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => deleteItem(idx)}
                                            size="large"
                                        >
                                            <DeleteForever />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                {action ? action(item) : null}
                                {Action ? <Action item={item} /> : null}
                            </Grid>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        </Paper>
    )
}

export default observer(ItemList)
